import { Button, IconButton, SwitchButtonGroup } from '@clef/client-library';
import { LabelType } from '@clef/shared/types';
import {
  Box,
  Chip,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import SearchIcon from '@material-ui/icons/Search';
import Autocomplete, {
  AutocompleteGetTagProps,
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
} from '@material-ui/lab/Autocomplete';
import React, { SetStateAction, useCallback, useState } from 'react';
import { rootElement } from '../../../utils/dom_utils';
import { PROJECT_TYPE, QueryTag, SortOption } from '../../../utils/project_utils';
import {
  ClientFeatures,
  useAnomalyDetectionEnabled,
  useFeatureGateEnabled,
} from '@/hooks/useFeatureGate';
import useStyles from '../styles';
import Sort from '@material-ui/icons/Sort';
import GridViewIcon from '@/pages/InstantLearningLabeling/icons/GridViewIcon';
import FormatListBulleted from '@material-ui/icons/FormatListBulleted';
import { ProjectsFilterOptions, ProjectsViewMode } from '@/uiStates/projects/projectsStates';
import { projectQueryKeys, useCreateProjectMutation } from '@/serverStore/projects';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import { queryClient } from '@/serverStore';
import { usageQueryKey } from '@/serverStore/usage';

export interface ProjectsToolbarProps {
  idPrefix?: string;
  searchText: string;
  setSearchText: (searchText: string) => void;
  sortOption: SortOption;
  setSortOption: (sortOption: SortOption) => void;
  queryTags: QueryTag[];
  setQueryTags: React.Dispatch<SetStateAction<QueryTag[]>>;
  filterOption: ProjectsFilterOptions;
  setFilterOption: React.Dispatch<SetStateAction<ProjectsFilterOptions>>;
  viewMode: ProjectsViewMode;
  setViewMode: React.Dispatch<SetStateAction<ProjectsViewMode>>;
}

const getOptions = (isVpEnabled: boolean, isAnomalyDetectionEnabled: boolean) => {
  return [
    { group: PROJECT_TYPE, label: t('Object Detection'), id: LabelType.BoundingBox },
    { group: PROJECT_TYPE, label: t('Segmentation'), id: LabelType.Segmentation },
    { group: PROJECT_TYPE, label: t('Classification'), id: LabelType.Classification },
    ...(isVpEnabled
      ? [
          {
            group: PROJECT_TYPE,
            label: t('Visual Prompting'),
            id: LabelType.SegmentationInstantLearning,
          },
        ]
      : []),
    ...(isAnomalyDetectionEnabled
      ? [
          {
            group: PROJECT_TYPE,
            label: t('Anomaly Detection'),
            id: LabelType.AnomalyDetection,
          },
        ]
      : []),
  ];
};

export interface ProjectToolbarAutocompleteProps {
  searchText: string;
  setSearchText: (searchText: string) => void;
  queryTags: QueryTag[];
  setQueryTags: React.Dispatch<SetStateAction<QueryTag[]>>;
}

export const ProjectToolbarAutocomplete: React.FC<ProjectToolbarAutocompleteProps> = ({
  searchText,
  setSearchText,
  queryTags,
  setQueryTags,
}) => {
  const styles = useStyles();
  const [groupedOptionsOpen, setGroupedOptionsOpen] = useState(false);
  const closePopper = () => setGroupedOptionsOpen(false);
  const openPopper = () => setGroupedOptionsOpen(true);
  const isVpEnabled = !useFeatureGateEnabled(ClientFeatures.DisableVisualPrompting);
  const isAnomalyDetectionEnabled = useAnomalyDetectionEnabled();
  const _renderInput = (params: AutocompleteRenderInputParams) => {
    return (
      <TextField
        {...params}
        variant="outlined"
        placeholder={t('Search by project name, project type, creator')}
        inputProps={{
          ...params.inputProps,
        }}
        InputProps={{
          ...params.InputProps,
          className: styles.projectToolbarSearchTextField,
          startAdornment: (
            <>
              <SearchIcon className={styles.searchIcon} />
              {params.InputProps.startAdornment}
            </>
          ),
        }}
      />
    );
  };

  const _renderGroup = (params: AutocompleteRenderGroupParams) => {
    return (
      <div key={params.key} className={styles.projectToolbarPopupGroup}>
        <div className={styles.projectToolbarPopupGroupHeader}>{params.group}</div>
        {params && params.children ? (
          <div className={styles.projectToolbarPopupGroupItemsContainer}>{params?.children}</div>
        ) : null}
      </div>
    );
  };

  const _renderQueryTag = (
    option: QueryTag,
    index: number,
    getTagProps: AutocompleteGetTagProps,
  ) => {
    return (
      <Chip
        size="small"
        {...getTagProps({ index })}
        label={option.label}
        onDelete={_e => {
          setQueryTags((prevQueryTags: QueryTag[]) => {
            return prevQueryTags.filter(item => {
              return item.id !== option.id;
            });
          });
        }}
        classes={{
          root: styles.projectToolbarQueryTagRoot,
          sizeSmall: styles.projectToolbarQueryTagSizeSmall,
        }}
      />
    );
  };

  return (
    <Autocomplete
      id="grouped-demo"
      value={queryTags}
      inputValue={searchText}
      open={groupedOptionsOpen}
      onBlur={closePopper}
      onOpen={openPopper}
      onClose={closePopper}
      onKeyDown={e => {
        if (e.key === 'Backspace' && searchText === '' && queryTags.length > 0) {
          setQueryTags(curQueryTags => {
            const clonedQueryTags = [...curQueryTags];
            clonedQueryTags.pop();
            return clonedQueryTags;
          });
        }
      }}
      onChange={(_event, value, reason) => {
        if (reason === 'select-option') {
          _event.defaultPrevented = true;
          setQueryTags((prevQueryTags: QueryTag[]) => {
            const queryTags = value.filter(option => {
              return typeof option !== 'string';
            }) as QueryTag[];
            if (!prevQueryTags.length) {
              return [...queryTags];
            }
            const newSelectedQueryTag = queryTags.find(queryTag => {
              return !prevQueryTags.map(prevQueryTag => prevQueryTag.id).includes(queryTag.id);
            });
            if (newSelectedQueryTag) {
              const filteredQueryTagsWithDifferentGroup = queryTags.filter(queryTag => {
                return queryTag.group !== newSelectedQueryTag.group;
              }) as QueryTag[];
              return [...filteredQueryTagsWithDifferentGroup, newSelectedQueryTag];
            }
            return [...queryTags];
          });
        } else if (reason === 'clear') {
          setQueryTags([]);
          setSearchText('');
        }
      }}
      onInputChange={(_event, value, reason) => {
        if (reason === 'input') {
          setSearchText(value);
        }
      }}
      renderTags={(value: readonly (string | QueryTag)[], getTagProps) =>
        value.map((option: string | QueryTag, index: number) => {
          if (typeof option !== 'string') {
            return _renderQueryTag(option, index, getTagProps);
          } else {
            return null;
          }
        })
      }
      renderGroup={_renderGroup}
      renderInput={_renderInput}
      className={styles.projectToolbarAutocomplete}
      classes={{
        option: styles.projectToolbarAutocompleteOption,
        input: styles.projectToolbarAutocompleteInput,
        endAdornment: styles.projectToolbarEndAdornment,
      }}
      ListboxProps={{
        className: styles.projectToolbarPopupBox,
      }}
      multiple={true}
      fullWidth={true}
      options={getOptions(!!isVpEnabled, !!isAnomalyDetectionEnabled)}
      freeSolo={true}
      autoSelect={true}
      disableCloseOnSelect={true}
      groupBy={option => option.group}
      getOptionLabel={option => option.label}
      filterOptions={(options, _state) => {
        // If there is selected option from a group, hide all options of the group from the dropdown
        const filteredOptions = options.filter(option => {
          return !queryTags.map(tag => tag.group).includes(option.group);
        });
        return filteredOptions;
      }}
    />
  );
};

export const ProjectsToolbar: React.FC<ProjectsToolbarProps> = ({
  queryTags,
  setQueryTags,
  searchText,
  setSearchText,
  sortOption,
  setSortOption,
  filterOption,
  setFilterOption,
  viewMode,
  setViewMode,
}) => {
  const styles = useStyles();
  const [dropdownButtonAnchorEl, setDropdownButtonAnchorEl] = useState<HTMLElement | null>(null);

  const onDropdownButtonClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setDropdownButtonAnchorEl(event.currentTarget);
  }, []);

  const onDropdownButtonClose = useCallback(() => {
    setDropdownButtonAnchorEl(null);
  }, []);

  const createProject = useCreateProjectMutation();
  const [isCreating, setIsCreating] = useState(false);
  const orgId = useTypedSelector(state => state.login.user)?.orgId!;

  const handleCreateProject = useCallback(async () => {
    if (isCreating) {
      return;
    }
    setIsCreating(true);
    await createProject();
    queryClient.invalidateQueries(usageQueryKey.summary(orgId));
    queryClient.invalidateQueries(projectQueryKeys.orgAdminList(orgId));
    setIsCreating(false);
  }, [createProject, isCreating, orgId]);

  return (
    <Box className={styles.projectsToolbarContainer} mb={6}>
      <Box display="flex" justifyContent="flex-end" mb={4}>
        <Box mr={3}>
          <ProjectToolbarAutocomplete
            searchText={searchText}
            setSearchText={setSearchText}
            queryTags={queryTags}
            setQueryTags={setQueryTags}
          />
        </Box>
        <Button
          id="create-project-button"
          variant="contained"
          color="primary"
          size="small"
          onClick={handleCreateProject}
          disabled={isCreating}
          startIcon={isCreating ? <CircularProgress size={20} /> : null}
        >
          {t('Create Project')}
        </Button>
      </Box>
      <Box display="flex">
        <Box display="flex" alignItems="center">
          <Button
            id="filter-all-projects"
            variant={filterOption === ProjectsFilterOptions.All ? 'tonal' : 'text'}
            size="small"
            onClick={() => setFilterOption(ProjectsFilterOptions.All)}
          >
            {t('All')}
          </Button>
          <Button
            id="filter-all-projects"
            variant={filterOption === ProjectsFilterOptions.OwnedByMe ? 'tonal' : 'text'}
            size="small"
            onClick={() => setFilterOption(ProjectsFilterOptions.OwnedByMe)}
          >
            {t('Owned by me')}
          </Button>
          <Button
            id="filter-all-projects"
            tooltip={t('Private projects shared with me')}
            variant={filterOption === ProjectsFilterOptions.SharedWithMe ? 'tonal' : 'text'}
            size="small"
            onClick={() => setFilterOption(ProjectsFilterOptions.SharedWithMe)}
          >
            {t('Shared with me')}
          </Button>
        </Box>
        <div style={{ flex: 1 }}></div>
        {viewMode === ProjectsViewMode.Grid && (
          <Button
            id="fre_projects_sorting_button"
            data-testid="fre_projects_sorting_button"
            aria-controls="fre_projects_sorting_menu"
            aria-haspopup="true"
            variant="text"
            onClick={onDropdownButtonClick}
            startIcon={<Sort />}
            endIcon={<KeyboardArrowDownIcon />}
            className={styles.projectGridSortingDropDownButton}
          >
            {t(sortOption)}
            <div style={{ flex: 1 }}></div>
          </Button>
        )}
        <Menu
          id="fre_projects_sorting_menu"
          data-testid="fre_projects_sorting_menu"
          elevation={0}
          // Required for placing the menu item below the dropdown button
          // https://github.com/mui/material-ui/issues/7961
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          anchorEl={dropdownButtonAnchorEl}
          open={!!dropdownButtonAnchorEl}
          onClose={onDropdownButtonClose}
          container={rootElement}
          keepMounted
        >
          {[SortOption.LastOpened, SortOption.DateCreatedNewest, SortOption.DateCreatedOldest].map(
            sortOption => (
              <MenuItem
                key={sortOption}
                onClick={() => {
                  setSortOption(sortOption);
                  onDropdownButtonClose();
                }}
              >
                <ListItemText primary={t(sortOption)} />
              </MenuItem>
            ),
          )}
        </Menu>

        <SwitchButtonGroup size="small">
          <Tooltip title={t('Grid view')}>
            <IconButton
              className={viewMode === ProjectsViewMode.Grid ? 'active' : undefined}
              onClick={() => setViewMode(ProjectsViewMode.Grid)}
            >
              <GridViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('List view')}>
            <IconButton
              className={viewMode === ProjectsViewMode.List ? 'active' : undefined}
              onClick={() => setViewMode(ProjectsViewMode.List)}
            >
              <FormatListBulleted />
            </IconButton>
          </Tooltip>
        </SwitchButtonGroup>
      </Box>
    </Box>
  );
};
